// src/utils/MoveHistoryManager.ts (UPDATED WITH TIMESTAMPS + AI CONTEXT)
import { Piece, Position } from "../types/Board.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";

// AI Position Context for heuristics verification (Position-Aware Heuristic System)
export interface MoveAIContext {
  game_phase: string;      // Opening, Early Middlegame, Late Middlegame, Endgame
  situation: string;       // Situation codes: OPN, MID, END, ATK, DEF, etc.
  is_emergency: boolean;   // Is this an emergency situation?
  primary_goal: string;    // Primary goal name
  goals: string;           // All active goals as comma-separated string
  profile: string;         // Profile type: ATTACK, DEFENSE, ENDGAME, DEVELOPMENT, BALANCED
}

export interface Move {
  id: string;
  pieceType: string;
  pieceColor: "white" | "black";
  from: Position;
  to: Position;
  capturedPiece?: {
    type: string;
    color: string;
    value?: number;
  };
  notation: string;
  isCheck: boolean;
  isCheckmate: boolean;
  isEnPassant?: boolean;
  enPassantCaptureSquare?: Position;
  promotedTo?: string;
  timestamp: Date; // NEW: Timestamp when move was made
  aiContext?: MoveAIContext; // AI heuristics context (only for AI moves)
}

export class MoveHistoryManager {
  private moves: Move[] = [];
  private boardSize: number = 8;
  private enPassantState: {
    canCapture: boolean;
    capturedPawnRank: number | null;
    capturedPawnFile: number | null;
    expiresAfterTurn: number;
  } = {
    canCapture: false,
    capturedPawnRank: null,
    capturedPawnFile: null,
    expiresAfterTurn: -1,
  };

  constructor(boardSize: number = 8) {
    this.boardSize = boardSize;
  }

  setBoardSize(size: number) {
    this.boardSize = size;
  }

  addMove(
    piece: Piece,
    from: Position,
    to: Position,
    capturedPiece?: Piece,
    isCheck: boolean = false,
    isCheckmate: boolean = false,
    isEnPassant: boolean = false,
    enPassantCaptureSquare?: Position,
    promotedTo?: string,
    aiContext?: MoveAIContext
  ): void {
    const notation = this.generateNotation(
      piece,
      from,
      to,
      capturedPiece,
      isCheck,
      isCheckmate,
      isEnPassant,
      promotedTo
    );

    const capturedPieceValue = capturedPiece
      ? this.getPieceValue(capturedPiece)
      : undefined;

    const move: Move = {
      id: `${Date.now()}-${Math.random()}`,
      pieceType: piece.type,
      pieceColor: piece.color,
      from,
      to,
      capturedPiece: capturedPiece
        ? {
            type: capturedPiece.type,
            color: capturedPiece.color,
            value: capturedPieceValue,
          }
        : undefined,
      notation,
      isCheck,
      isCheckmate,
      isEnPassant,
      enPassantCaptureSquare,
      promotedTo,
      timestamp: new Date(), // NEW: Capture current timestamp
      aiContext, // AI heuristics context (only for AI moves)
    };

    this.moves.push(move);

    // Update en passant state
    if (
      piece.type.toLowerCase() === "pawn" &&
      Math.abs(to.row - from.row) === 2
    ) {
      this.enPassantState = {
        canCapture: true,
        capturedPawnRank: to.row,
        capturedPawnFile: to.col,
        expiresAfterTurn: this.moves.length + 1,
      };
    } else if (this.moves.length >= this.enPassantState.expiresAfterTurn) {
      this.enPassantState = {
        canCapture: false,
        capturedPawnRank: null,
        capturedPawnFile: null,
        expiresAfterTurn: -1,
      };
    }
  }

  getMoves(): Move[] {
    return this.moves;
  }

  getMovesWithNumbers(): Array<Move & { moveNumber: string }> {
    // FIX: Calculate move numbers based on actual colors, not array index
    let whiteCount = 0;
    let blackCount = 0;
    
    return this.moves.map((move) => {
      let moveNumber: string;
      
      if (move.pieceColor === "white") {
        whiteCount++;
        moveNumber = `${whiteCount}.`;
      } else {
        blackCount++;
        moveNumber = `${blackCount}...`;
      }
      
      return { ...move, moveNumber };
    });
  }

  getMoveCount(): number {
    return this.moves.length;
  }

  clear(): void {
    this.moves = [];
    this.enPassantState = {
      canCapture: false,
      capturedPawnRank: null,
      capturedPawnFile: null,
      expiresAfterTurn: -1,
    };
  }

  getEnPassantState() {
    return this.enPassantState;
  }

  isEnPassantLegal(
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    color: "white" | "black"
  ): boolean {
    if (!this.enPassantState.canCapture) {
      return false;
    }

    const capturedPawnRank = this.enPassantState.capturedPawnRank!;
    const capturedPawnFile = this.enPassantState.capturedPawnFile!;

    if (toCol !== capturedPawnFile) {
      return false;
    }

    if (color === "white") {
      if (fromRow !== capturedPawnRank || toRow !== capturedPawnRank - 1) {
        return false;
      }
    } else {
      if (fromRow !== capturedPawnRank || toRow !== capturedPawnRank + 1) {
        return false;
      }
    }

    if (Math.abs(toCol - fromCol) !== 1) {
      return false;
    }

    return true;
  }

  private generateNotation(
    piece: Piece,
    from: Position,
    to: Position,
    capturedPiece?: Piece,
    isCheck: boolean = false,
    isCheckmate: boolean = false,
    isEnPassant: boolean = false,
    promotedTo?: string
  ): string {
    let notation = "";

    if (piece.type.toLowerCase() === "pawn") {
      if (capturedPiece || isEnPassant) {
        notation += this.getFileLabel(from.col);
      }
    } else {
      notation += piece.abbreviation.toUpperCase();
    }

    if (capturedPiece || isEnPassant) {
      notation += "x";
    }

    notation += this.getFileLabel(to.col) + this.getRankLabel(to.row);

    if (isEnPassant) {
      notation += " e.p.";
    }

    if (promotedTo) {
      const promotedPieceDef = PIECE_DEFINITIONS[promotedTo.toLowerCase()];
      notation += "=" + (promotedPieceDef?.abbreviation.toUpperCase() || "Q");
    }

    if (isCheckmate) {
      notation += "#";
    } else if (isCheck) {
      notation += "+";
    }

    return notation;
  }

  private getFileLabel(col: number): string {
    if (col < 26) {
      return String.fromCharCode(97 + col);
    } else {
      const firstLetter = String.fromCharCode(97 + Math.floor(col / 26) - 1);
      const secondLetter = String.fromCharCode(97 + (col % 26));
      return firstLetter + secondLetter;
    }
  }

  private getRankLabel(row: number): string {
    return String(this.boardSize - row);
  }

  private getPieceValue(piece: Piece): number {
    const boardSizeStr = `${this.boardSize}×${this.boardSize}`;
    const definition = PIECE_DEFINITIONS[piece.type.toLowerCase()];

    if (!definition) return 0;

    const abbreviation = definition.abbreviation.toUpperCase();

    try {
      const pieceValuesModule = require("../data/pieceValues.ts");
      const pieceValues = pieceValuesModule.PIECE_VALUES[abbreviation];

      if (pieceValues) {
        const valueEntry = pieceValues.find(
          (v: any) => v.boardSize === boardSizeStr
        );
        if (valueEntry) {
          return valueEntry.value;
        }
      }
    } catch (error) {
      console.warn(`Could not get value for piece: ${piece.type}`);
    }

    return definition.value || 0;
  }

  getMaterialAdvantage(): number {
    let whiteValue = 0;
    let blackValue = 0;

    this.moves.forEach((move) => {
      if (move.capturedPiece && move.capturedPiece.value !== undefined) {
        if (move.capturedPiece.color === "white") {
          blackValue += move.capturedPiece.value;
        } else {
          whiteValue += move.capturedPiece.value;
        }
      }
    });

    return whiteValue - blackValue;
  }

  getCapturedPiecesValue(): { white: number; black: number } {
    let whiteValue = 0;
    let blackValue = 0;

    this.moves.forEach((move) => {
      if (move.capturedPiece && move.capturedPiece.value !== undefined) {
        if (move.capturedPiece.color === "white") {
          blackValue += move.capturedPiece.value;
        } else {
          whiteValue += move.capturedPiece.value;
        }
      }
    });

    return { white: whiteValue, black: blackValue };
  }

  // NEW: Get formatted timestamp for a move
  getFormattedTimestamp(move: Move): string {
    const now = new Date();
    const moveTime = new Date(move.timestamp);
    const diffMs = now.getTime() - moveTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {
      return "Just now";
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      return `${hours}h ago`;
    } else {
      return moveTime.toLocaleDateString();
    }
  }

  // NEW: Get absolute time for a move
  getAbsoluteTime(move: Move): string {
    const moveTime = new Date(move.timestamp);
    return moveTime.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  }
}
